import React, { useState } from 'react';
import { Box, Typography, Chip, Grid, Divider, Stack } from '@mui/material';
import { InfoRow } from './InfoRow';
import { IDataprevEmployee } from 'services/zetra';
import { ReactComponent as MoneyCheckIcon } from 'assets/money-check.svg';
import { ReactComponent as MoneyRemoveIcon } from 'assets/money-remove.svg';
import { ReactComponent as WarningErrorIcon } from 'assets/warning-error.svg';
import { Drawer, RightIcon } from '@uy3/web-components';

import { formatCpfOrCnpj, maskCurrencyInCents } from 'helpers';
import { ContractOptions } from './ContractOptions';

export interface EmployerCardField {
    label: string;
    value: string | number | React.ReactNode;
}

interface EmployerCardProps {
    fields: EmployerCardField[];
    headerData?: IDataprevEmployee;
    updateQueryData?: () => void;
}

const FIELDS_PER_ROW = 5;

export const EmployerCard: React.FC<EmployerCardProps> = ({ fields, headerData }) => {
    const [openDrawer, setOpenDrawer] = useState(false);

    const rows = fields.reduce<EmployerCardField[][]>((acc, curr, idx) => {
        if (idx % FIELDS_PER_ROW === 0) acc.push([]);
        acc[acc.length - 1].push(curr);
        return acc;
    }, []);

    const handleDrawerClose = () => {
        setOpenDrawer(false);
    };

    const handleDrawerOpen = () => {
        setOpenDrawer(true);
    };

    return (
        <Stack
            sx={{
                width: '100%',
                p: 5,
                borderRadius: 2,
                backgroundColor: '#F4F3F3',
                mb: 2
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 2
                }}
            >
                <Box>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontFamily: 'Lato',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '120%',
                            letterSpacing: '-0.2px',
                            mb: 0.5
                        }}
                    >
                        {`${headerData?.nomeEmpregador} - ${formatCpfOrCnpj(
                            String(headerData?.numeroInscricaoEmpregador)
                        )}`}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            fontFamily: 'Lato',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '120%',
                            letterSpacing: '-0.2px'
                        }}
                    >
                        Empregador
                    </Typography>
                </Box>
                <Chip
                    color={headerData?.valorMargemDisponivel === 0 ? 'default' : 'success'}
                    sx={{
                        fontWeight: 500,
                        mt: { xs: 2, sm: 0 },
                        backgroundColor:
                            headerData?.valorMargemDisponivel === 0 ? '#000' : '#7BC67E',
                        fontSize: '16px',
                        px: 1,
                        minWidth: '180px',
                        width: 'auto',
                        height: '40px',
                        borderRadius: '360px',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        gap: '12px',
                        '& .MuiChip-label': {
                            px: 1,
                            fontFamily: 'Lato',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '120%',
                            letterSpacing: '-0.2px',
                            color: headerData?.valorMargemDisponivel === 0 ? '#FFFFFF' : '#014303',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }
                    }}
                    label={
                        headerData?.valorMargemDisponivel === 0 ? (
                            <React.Fragment>
                                <MoneyRemoveIcon />
                                {'Não possui margem disponível'}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <MoneyCheckIcon />
                                <span>{'Margem disponível: '}</span>
                                {maskCurrencyInCents(headerData?.valorMargemDisponivel)}
                            </React.Fragment>
                        )
                    }
                />
            </Box>

            <Box>
                {rows.map((row, rowIdx) => (
                    <React.Fragment key={rowIdx}>
                        <Grid container spacing={0} alignItems="stretch">
                            {row.map((field, colIdx) => (
                                <Grid item xs={12} sm={6} md={2.4} key={colIdx}>
                                    <InfoRow label={field.label} value={field.value} />
                                </Grid>
                            ))}
                        </Grid>
                        {rowIdx < rows.length - 1 && <Divider sx={{ my: 2 }} />}
                    </React.Fragment>
                ))}
            </Box>

            {headerData?.emprestimosLegados && headerData.emprestimosLegados.length > 0 && (
                <Box
                    sx={{
                        background: '#FFDFAE',
                        p: 3,
                        height: 68,
                        borderRadius: 2,
                        mt: 5
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Stack direction="row" alignItems="center" gap={1}>
                            <WarningErrorIcon />
                            <Typography
                                sx={{
                                    fontFamily: 'Lato',
                                    fontWeight: 600,
                                    fontSize: 18,
                                    lineHeight: '120%',
                                    letterSpacing: '-0.2px'
                                }}
                            >
                                Empréstimo legado informado pela instituição financeira
                            </Typography>
                        </Stack>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            sx={{
                                cursor: 'pointer',
                                transition: 'background 0.2s, box-shadow 0.2s',
                                borderRadius: 2
                            }}
                            onClick={handleDrawerOpen}
                        >
                            <RightIcon
                                sx={{
                                    height: 24,
                                    width: 24
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: 'Lato',
                                    fontWeight: 600,
                                    fontSize: 16,
                                    lineHeight: '120%',
                                    letterSpacing: '-0.2px',
                                    color: '#000',
                                    cursor: 'pointer'
                                }}
                            >
                                Ver detalhes
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            )}
            <Drawer
                title="Empréstimo legado"
                description="Registro de empréstimo pré-existente, informado pela instituição financeira."
                anchor="right"
                open={openDrawer}
                onClose={handleDrawerClose}
            >
                <Stack spacing={1} justifyContent="right" direction="column">
                    <ContractOptions
                        handleDrawerClose={handleDrawerClose}
                        headerData={headerData}
                    />
                </Stack>
            </Drawer>
        </Stack>
    );
};
